import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { DashboardGrid } from '@/components/dashboard/DashboardGrid';
import { DynamicDashboardWidget } from '@/components/dashboard/DynamicDashboardWidget';
import { DashboardWidgetRenderer } from '@/components/dashboard/DashboardWidgetRenderer';
import { DashboardToolbarActions } from '@/components/dashboard/DashboardToolbarActions';
import { TimeRangeSelector } from '@/components/dashboard/TimeRangeSelector';
import { WidgetEditOverlay } from '@/components/dashboard/WidgetEditOverlay';
import { WidgetEditDialog } from '@/components/dashboard/WidgetEditDialog';
import { useDashboardWidgets } from '@/hooks/dashboard/useDashboardWidgets';
import { useRegisterHomeDashboard } from '@/contexts/HomeDashboardContext';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';

/** Widget types that fetch their own data (not through DynamicDashboardWidget) */
const SELF_CONTAINED_TYPES = new Set(['taskInbox']);

const HOME_TIME_RANGES = [
  { key: 'last7days', label: 'Last 7 days', resolution: 'day' },
  { key: 'last30days', label: 'Last 30 days', resolution: 'day' },
  { key: 'last90days', label: 'Last 90 days', resolution: 'day' },
  { key: 'last12months', label: 'Last 12 months', resolution: 'month' },
  { key: 'ytd', label: 'Year to date', resolution: 'month' },
];

export function HomeDashboard() {
  const { t } = useTranslation();

  const {
    widgets,
    layout,
    isLoading,
    isEditing,
    setIsEditing,
    onLayoutChange,
    addWidget,
    updateWidget,
    removeWidget,
    saveLayout,
    resetToDefaults,
    isSaving,
  } = useDashboardWidgets('Home', []);

  const [editingWidget, setEditingWidget] = useState<WidgetConfig | null>(null);
  const [timeRangeKey, setTimeRangeKey] = useState('last30days');

  const filters = useMemo(() => {
    const tr = HOME_TIME_RANGES.find((t) => t.key === timeRangeKey);
    return {
      timeRangeKey,
      resolution: tr?.resolution ?? 'day',
      scope: { legalEntityId: 'current' as string | undefined, businessUnitId: undefined as string | undefined },
    };
  }, [timeRangeKey]);

  const timeRangeLabel = HOME_TIME_RANGES.find((t) => t.key === timeRangeKey)?.label ?? '';

  useRegisterHomeDashboard(setIsEditing);

  const isEmpty = widgets.length === 0 && !isEditing && !isLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {isEditing ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, px: 2, pt: 1.5 }}>
          <TimeRangeSelector
            timeRanges={HOME_TIME_RANGES}
            value={timeRangeKey}
            onChange={setTimeRangeKey}
          />
          <DashboardToolbarActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit
            onToggleEdit={setIsEditing}
            onSave={saveLayout}
            onReset={resetToDefaults}
            onAddWidget={addWidget}
          />
        </Box>
      ) : !isEmpty && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, px: 2, pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {timeRangeLabel}
          </Typography>
          <DashboardToolbarActions
            isEditing={isEditing}
            isSaving={isSaving}
            canEdit
            onToggleEdit={setIsEditing}
            onSave={saveLayout}
            onReset={resetToDefaults}
            onAddWidget={addWidget}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60%' }}>
            <CircularProgress />
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60%',
              gap: 2,
            }}
          >
            <DashboardCustomizeIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.4 }} />
            <Typography variant="h6" color="text.secondary">
              {t('dashboard.welcomeTitle', 'Welcome to your Home Dashboard')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, textAlign: 'center' }}>
              {t(
                'dashboard.welcomeDescription',
                'Pin widgets from any dashboard or add new ones to build your personalized overview.',
              )}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DashboardCustomizeIcon />}
              onClick={() => setIsEditing(true)}
            >
              {t('dashboard.customizeDashboard', 'Customize Dashboard')}
            </Button>
          </Box>
        ) : (
          <DashboardGrid
            layout={layout}
            rowHeight={120}
            editable={isEditing}
            onLayoutChange={onLayoutChange}
          >
            {widgets.map((w) => (
              <div key={w.id}>
                <WidgetEditOverlay
                  editing={isEditing}
                  canEdit={!SELF_CONTAINED_TYPES.has(w.type)}
                  onRemove={() => removeWidget(w.id)}
                  onEdit={() => setEditingWidget(w)}
                >
                  {SELF_CONTAINED_TYPES.has(w.type) ? (
                    <DashboardWidgetRenderer widget={w} />
                  ) : (
                    <DynamicDashboardWidget widget={w} filters={filters} />
                  )}
                </WidgetEditOverlay>
              </div>
            ))}
          </DashboardGrid>
        )}
      </Box>

      <WidgetEditDialog
        open={editingWidget !== null}
        widget={editingWidget}
        onClose={() => setEditingWidget(null)}
        onSave={updateWidget}
      />
    </Box>
  );
}
